/**
 * The live "Metal Insights & Resources" section: teasers for the three newest Metal Market
 * Intelligence blog articles, with a link to the blog landing page. It replaces
 * `./MetalInsightsAndResources.tsx`, which has no live caller.
 *
 * Position:
 * - Rendered near the foot of the metal-solutions capabilities, industries and solutions pages
 *   (for example `pages/metal-solutions/industries/climate/index.tsx`,
 *   `components/Solutions/BasePage/BasePage.tsx`,
 *   `components/BarTubeStructuralProcessing/BasePage/BasePage.tsx`) and below a blog article in
 *   `components/Blog/Blog.tsx`.
 * - The articles come from the page's `getStaticProps` →
 *   `src/contentful/blog/getContentfulBlogEntries.tsx` → `getContentfulBlogEntries`, which
 *   returns them per language, newest first, three by default. This component takes the first
 *   three of the visitor's language whatever it is given.
 * - Pages also import {@link defaultStaticContent} to label the section in their section
 *   navigation (`title1 + " " + titleSeparator + " " + title2`).
 *
 * Desktop (`lg` and up): a scrolling marquee title, then one row per article (hero image, title
 * linked to the article, SEO description, category badges). Mobile: one card per article, the
 * whole card linked to the article, with the badges over the image. Hero images are requested from
 * the Contentful Images API as 680×180 JPEGs.
 */
import styled from "@emotion/styled";
import { keyframes, css } from "@emotion/react";
import React from "react";
import { useTheme } from "@ryerson/frontend.theme";
import { ContentSection } from "@ryerson/frontend.layout";
import { Typography } from "@ryerson/frontend.typography";
import { useApplication } from "@ryerson/frontend.application";
import { Badge } from "@ryerson/frontend.notification";
import { Media } from "@ryerson/frontend.next";
import { LanguageContent } from "@components/Shared/model/LanguageContent";
import { Link, ButtonLink } from "@ryerson/frontend.navigation";
import { TranslatedBlogEntries } from "@contentful/blog/types";
import Spacer from "../Spacer/Spacer";
/**
 * The section's fixed copy. The title is `title1`, `titleSeparator`, `title2` (the separator is
 * drawn in the text colour, the two halves in the header colour). `actionLabel` is passed to
 * `ButtonLink` → `label`; `actionUrl` is the blog landing page.
 */
export type MetalInsightsStaticContent = {
	title1: string;
	title2: string;
	titleSeparator: string;
	actionUrl: string;
	actionLabel: string | string[];
};

/** `allBlogsData`: blog articles per language, newest first, from `getContentfulBlogEntries`. */
export type MetalInsightsAndResourcesProps = {
	allBlogsData: TranslatedBlogEntries;
};

const Divider = styled.div`
	display: block;
	width: 100%;
	height: 1px;
	margin: 0px;
`;

const BlogItem = styled.div`
	display: block;
	width: 100%;
	height: 180px;
`;

const BlogItemImage = styled.div`
	display: inline-block;
	width: 300px;
	margin-right: 100px;
	vertical-align: top;
	height: 180px;
	background-repeat: no-repeat;
	background-position: center center;
	background-size: cover;
`;

const BlogTitle = styled.div`
	display: inline-block;
	width: 260px;
	vertical-align: top;
	position: relative;
	height: 180px;
	margin-right: 140px;
`;

const BlogTitleBadge = styled.div`
	display: block;
	width: 100%;
	left: 0;
	bottom: 0;
	height: auto;
	padding-top: 30px;
	position: relative;
`;

const BlogBlurb = styled.div`
	display: inline-block;
	width: calc(100% - 800px);
	padding-top: 5px;
	box-sizing: border-box;
	height: 180px;
`;

const MobileBlogItem = styled.div`
	display: block;
	width: 100%;
	margin-bottom: 40px;
	height: auto;
`;

const MobileBlogItemImage = styled.div`
	display: block;
	width: 100%;
	position: relative;
	height: 180px;
	background-repeat: no-repeat;
	background-position: center center;
	background-size: cover;
`;

const MobileBlogTitleBadge = styled.div`
	display: block;
	width: 100%;
	top: 20px;
	left: 20px;
	height: 24px;
	width: calc(100% - 40px);
	position: absolute;
`;



/**
 * The section's copy per language. Also imported by the pages that render the section, to build
 * its label in their section navigation, so a change to the title changes those labels too.
 */
export const defaultStaticContent: LanguageContent<MetalInsightsStaticContent> = {
	en: {
		title1: "Metal Insights",
		title2: "Resources",
		titleSeparator: "&",
		actionUrl: "/metal-resources/metal-market-intelligence",
		actionLabel: ["More Metal Insights", "& Resources"],
	},
	fr: {
		title1: "Ressources et de connaissances",
		title2: "matière de métaux",
		titleSeparator: "en",
		actionUrl: "/metal-resources/metal-market-intelligence",
		actionLabel: ["Plus de ressources et de connaissances", "en matière de métaux"],
	},
	es: {
		title1: "Datos",
		title2: "recursos sobre metales",
		titleSeparator: "y",
		actionUrl: "/metal-resources/metal-market-intelligence",
		actionLabel: ["Más datos relevantes", "y recursos sobre el metal"],
	},
};

const SideScroll = keyframes`
    0% {
        -moz-transform: translateX(100%);
        -webkit-transform: translateX(100%);
        transform: translateX(100%);
    }
    100% {
        -moz-transform: translateX(-100%);
        -webkit-transform: translateX(-100%);
        transform: translateX(-100%);
    }
`;

const Marquee = styled.div`
	display: block;
	width: 100%;
	height: 170px;
	overflow: hidden;
	position: relative;
`;

/**
 * Renders the section for the visitor's language from the first three entries of `allBlogsData`.
 */
const MetalInsightsAndResources: React.FC<MetalInsightsAndResourcesProps> = ({ allBlogsData }) => {
	const { theme } = useTheme();
	const {
		localization: { language },
	} = useApplication();

	const staticContent = defaultStaticContent[language];

	let translateBlogData = allBlogsData[language];

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<Spacer
					css={css`
						height: 80px;
						background-color: ${theme.colors.primary.background};
					`}
				/>
				<Marquee
					css={css`
						background-color: ${theme.colors.primary.background};
					`}
				>
					<Typography
						variant="h1"
						css={css`
							font-size: 150px;
							letter-spacing: -6px;
							position: absolute;
							width: 100%;
							height: 100%;
							margin: 0;
							line-height: 150px !important;
							white-space: nowrap;
							text-align: center;
							-moz-transform: translateX(100%);
							-webkit-transform: translateX(100%);
							transform: translateX(100%);
							-moz-animation: ${SideScroll} 8s linear infinite;
							-webkit-animation: ${SideScroll} 8s linear infinite;
							animation: ${SideScroll} 8s linear infinite;
						`}
					>
						<Typography
							variant="span"
							font="inherit"
							color={theme.colors.primary.header}
							weight="bold"
						>
							{staticContent.title1}{" "}
						</Typography>
						<Typography
							variant="span"
							font="inherit"
							color={theme.colors.primary.text}
							weight="bold"
						>
							{staticContent.titleSeparator}{" "}
						</Typography>
						<Typography
							variant="span"
							font="inherit"
							color={theme.colors.primary.header}
							weight="bold"
						>
							{staticContent.title2}
						</Typography>
					</Typography>
				</Marquee>
				<ContentSection type="primary" vPadding="80px">
					{translateBlogData.slice(0, 3).map((blog, index: number) => {
						const heroUrl = blog?.heroImage?.file?.url
							? blog?.heroImage?.file?.url + "?fm=jpg&fit=fill&w=680&h=180"
							: "";
						return (
							<React.Fragment key={index}>
								<BlogItem>
									<BlogItemImage
										css={css`
											background-image: url(${heroUrl});
										`}
									/>
									<BlogTitle>
										<Link
											to={
												"/metal-resources/metal-market-intelligence/" +
												blog?.slug
											}
										>
											<Typography
												variant="h3"
												type="primary"
												css={css`
													margin: 0px;
												`}
											>
												{blog?.title}
											</Typography>
										</Link>
									</BlogTitle>
									<BlogBlurb>
										<Typography variant="div" size="md" type="primary">
											{blog?.seoDescription}
										</Typography>
									</BlogBlurb>
								</BlogItem>
								<BlogTitleBadge>
									{blog.categories &&
										blog?.categories.length > 0 &&
										blog?.categories.map((category, index: number) => {
											return (
												<div
													key={index}
													css={css`
														display: inline-block;
														vertical-align: top;
														margin-bottom: 5px;
														margin-right: 5px;
													`}
												>
													<Badge
														color={category.categoryColor}
														type="primary"
														key={index}
													>
														{category.title}
													</Badge>
												</div>
											);
										})}
								</BlogTitleBadge>
								<Divider
									css={css`
										border-bottom: 1px solid ${theme.colors.primary.darkerGray};
										opacity: 0.1;
										margin-top: 40px;
										margin-bottom: 39px;
									`}
								/>
							</React.Fragment>
						);
					})}
					<Typography
						variant="div"
						css={css`
							display: block;
							width: 100%;
							margin-top: 60px;
							margin-bottom: 60px;
						`}
					>
						<ButtonLink
							label={staticContent.actionLabel}
							to={staticContent.actionUrl}
							orientation="left"
							size="md"
						/>
					</Typography>
				</ContentSection>
			</Media>
			<Media lessThan="lg">
				<ContentSection type="primary" vPadding="35px" hPadding="0px">
					{translateBlogData.slice(0, 3).map((blog, index: number) => {
						const heroUrl = blog?.heroImage?.file?.url
							? blog?.heroImage?.file?.url + "?fm=jpg&fit=fill&w=680&h=180"
							: "";
						return (
							<Link
								to={"/metal-resources/metal-market-intelligence/" + blog?.slug}
								key={index}
							>
								<MobileBlogItem>
									<MobileBlogItemImage
										css={css`
											background-image: url(${heroUrl});
										`}
									>
										<MobileBlogTitleBadge>
											{blog.categories &&
												blog?.categories.length > 0 &&
												blog?.categories.map(
													(category: any, index: number) => {
														return (
															<div
																key={index}
																css={css`
																	display: inline-block;
																	vertical-align: top;
																	margin-bottom: 5px;
																	margin-right: 5px;
																`}
															>
																<Badge
																	color={category.categoryColor}
																	type="primary"
																	key={index}
																>
																	{category.title}
																</Badge>
															</div>
														);
													}
												)}
										</MobileBlogTitleBadge>
									</MobileBlogItemImage>
									<Typography
										variant="h3"
										type="primary"
										css={css`
											margin-top: 25px;
											margin-bottom: 16px;
										`}
									>
										{blog?.title}
									</Typography>
									<Typography variant="div" size="md" type="primary">
										{blog?.seoDescription}
									</Typography>
								</MobileBlogItem>
							</Link>
						);
					})}
					<Divider
						css={css`
							border-bottom: 1px solid ${theme.colors.primary.darkerGray};
							opacity: 0.1;
							margin-top: 35px;
							margin-bottom: 40px;
						`}
					/>
					<Typography
						variant="div"
						css={css`
							display: block;
							width: 100%;
							margin-bottom: 30px;
						`}
					>
						<ButtonLink
							label={staticContent.actionLabel}
							to={staticContent.actionUrl}
							orientation="left"
							size="md"
						/>
					</Typography>
				</ContentSection>
			</Media>
		</>
	);
};

export default MetalInsightsAndResources;
